import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get } from "@/lib/api";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import { MODULES } from "@/lib/modules";
import { Target, Building2, KanbanSquare, SquareStack, Plus, ArrowRight, BookOpen } from "lucide-react";

const RECENT_KEY = "iris-recent";
export const pushRecent = (item) => {
  const cur = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").filter((r) => r.path !== item.path);
  localStorage.setItem(RECENT_KEY, JSON.stringify([item, ...cur].slice(0, 6)));
};

export function CommandPalette({ open, setOpen }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const recent = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    let active = true;
    const t = setTimeout(async () => {
      const data = await get(`/search?q=${encodeURIComponent(q)}`);
      if (active) setResults(data);
    }, 200);
    return () => { active = false; clearTimeout(t); };
  }, [q]);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };

  const actions = [
    { label: "New Lead", path: "/crm/leads?new=1", icon: Plus },
    { label: "New Project", path: "/pm/projects/new", icon: Plus },
    { label: "Go to Dashboard", path: "/", icon: ArrowRight },
  ].filter((a) => !q || a.label.toLowerCase().includes(q.toLowerCase()));

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." value={q} onValueChange={setQ} data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>No results for "{q}"</CommandEmpty>

        {!q && recent.length > 0 && (
          <CommandGroup heading="Recently Viewed">
            {recent.map((r) => (
              <CommandItem key={r.path} onSelect={() => go(r.path)}>
                <SquareStack className="mr-2 h-4 w-4 text-muted-foreground" />{r.title}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {actions.length > 0 && (
          <CommandGroup heading="Actions">
            {actions.map((a) => (
              <CommandItem key={a.label} onSelect={() => go(a.path)} data-testid={`cmd-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <a.icon className="mr-2 h-4 w-4 text-primary" />{a.label}
                <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">Action</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results?.leads?.length > 0 && (
          <CommandGroup heading="CRM · Leads">
            {results.leads.map((l) => (
              <CommandItem key={l.id} onSelect={() => go(`/crm/leads/${l.id}`)}>
                <Target className="mr-2 h-4 w-4 text-muted-foreground" />
                <span>{l.title}</span><span className="ml-2 text-xs text-muted-foreground">{l.sub}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results?.clients?.length > 0 && (
          <CommandGroup heading="CRM · Clients">
            {results.clients.map((c) => (
              <CommandItem key={c.id} onSelect={() => go(`/crm/clients/${c.id}`)}>
                <Building2 className="mr-2 h-4 w-4 text-muted-foreground" />{c.title}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results?.projects?.length > 0 && (
          <CommandGroup heading="Projects">
            {results.projects.map((p) => (
              <CommandItem key={p.id} onSelect={() => go(`/pm/projects/${p.id}`)}>
                <KanbanSquare className="mr-2 h-4 w-4 text-muted-foreground" />{p.title}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results?.items?.length > 0 && (
          <CommandGroup heading="Tickets">
            {results.items.map((it) => (
              <CommandItem key={it.id} onSelect={() => go(`/pm/projects/${it.project_id}/items/${it.id}`)}>
                <SquareStack className="mr-2 h-4 w-4 text-muted-foreground" />{it.title}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results?.kb?.length > 0 && (
          <CommandGroup heading="Knowledge Base">
            {results.kb.map((k) => (
              <CommandItem key={k.id} onSelect={() => go(`/kb`)}>
                <BookOpen className="mr-2 h-4 w-4 text-muted-foreground" />{k.title}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {!q && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Navigation">
              {MODULES.map((m) => (
                <CommandItem key={m.key} onSelect={() => go(m.path)}>
                  <m.icon className="mr-2 h-4 w-4 text-muted-foreground" />{m.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
